import React from "react";
import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import { getOrParseDateTime } from "@cpt/shared";
import { formatMoney } from "utils/formatters/CurrencyFormatters";
import { formatDateOnly } from "utils/formatters/DateFormatters";
import { OrderStatus } from "client/api-v2";
import type { OrderDto, OrderFulfillmentDto, PriceBreakdown } from "client/api-v2";
import { EmptyValue } from "components/MutedPlaceholder";
import type { OrderDraft } from "areas/orders/builder/useOrderDraft";
import { FulfilmentPanel } from "areas/orders/components/FulfilmentPanel";
import { PrimaryLifecycleAction, SecondaryLifecycleActions } from "areas/orders/components/OrderLifecycleActions";
import { currentTaxEstimate, taxRateLabel } from "areas/orders/utils/orderTaxEstimate";

export const standardQuoteExpiry = "30 days from publish";

function SummaryRow({ label, value, emphasis }: { label: string; value: React.ReactNode; emphasis?: boolean }) {
  const variant = emphasis ? "subtitle2" : "body2";
  return (
    <Stack direction="row" spacing={1.5} sx={{ justifyContent: "space-between" }}>
      <Typography variant={variant} color={emphasis ? "text.primary" : "text.secondary"}>
        {label}
      </Typography>
      <Typography variant={variant} sx={{ textAlign: "right" }}>
        {value}
      </Typography>
    </Stack>
  );
}

function statusSummary(status: OrderStatus): string {
  return status === OrderStatus.Quote ? "Quote (not yet accepted)" : status;
}

function termSummary(order: OrderDto): string {
  const months = Math.round(
    getOrParseDateTime(order.termEndDate).diff(getOrParseDateTime(order.termStartDate), "months").months
  );
  return `${months} months`;
}

function quoteExpirySummary(order: OrderDto, draft: OrderDraft): string {
  const expiry = draft.validUntilOverride || order.validUntil;
  return expiry ? formatDateOnly(getOrParseDateTime(expiry)) : standardQuoteExpiry;
}

function lockedHint(order: OrderDto): string | undefined {
  if (order.status === OrderStatus.Cancelled) {
    return "This order is canceled and can no longer be shared.";
  }
  if (order.status === OrderStatus.Accepted) {
    return undefined;
  }
  return order.status === OrderStatus.Expired ? "This quote has expired and can no longer be shared." : undefined;
}

export function OrderSummaryRail({
  order,
  draft,
  price,
  isDirty,
  readOnly,
  onOrderChanged,
  fulfillment,
  onFulfillmentChanged,
}: {
  order: OrderDto;
  draft: OrderDraft;
  price: PriceBreakdown;
  isDirty: boolean;
  readOnly: boolean;
  onOrderChanged: (order: OrderDto) => void;
  fulfillment?: OrderFulfillmentDto;
  onFulfillmentChanged: () => void;
}) {
  const isPublished = order.status === OrderStatus.Quote;
  const canAct = !readOnly;
  const taxEstimate = isDirty ? undefined : currentTaxEstimate(order.taxEstimate);
  const hint = lockedHint(order);

  return (
    <>
      <Box>
        <Paper sx={{ p: 2, mb: "10px" }}>
          <Stack spacing={1.25}>
            <Typography variant="h6" component="h2">
              Order summary
            </Typography>
            <SummaryRow
              label="Quote reference"
              value={
                <Box component="span" sx={{ fontFamily: "monospace" }}>
                  {order.odNumber}
                </Box>
              }
            />
            <SummaryRow label="Status" value={statusSummary(order.status)} />
            <SummaryRow label="Motion" value={order.salesMotion} />
            <SummaryRow label="Term" value={termSummary(order)} />
            <SummaryRow label="Quote expiry" value={quoteExpirySummary(order, draft)} />
            <Divider />
            <SummaryRow label="Total" value={formatMoney(price.total, price.currency)} emphasis />
            <SummaryRow
              label={`Tax estimate (${taxRateLabel(taxEstimate)})`}
              value={taxEstimate ? formatMoney(taxEstimate.estimatedTaxAmount, order.price.currency) : <EmptyValue />}
            />
            {taxEstimate && (
              <SummaryRow
                label="Total (incl. tax estimate)"
                value={formatMoney(taxEstimate.estimatedTotalIncludingTax, order.price.currency)}
              />
            )}
            <Divider />
            {canAct && (
              <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", justifyContent: "space-between" }}>
                {isPublished && (
                  <Typography variant="body2" color="success.main">
                    Quote published
                  </Typography>
                )}
                <PrimaryLifecycleAction order={order} onChanged={onOrderChanged} isDirty={isDirty} />
              </Stack>
            )}
            {hint && (
              <Typography variant="body2" color="text.secondary">
                {hint}
              </Typography>
            )}
          </Stack>
        </Paper>

        {canAct && <SecondaryLifecycleActions order={order} onChanged={onOrderChanged} isDirty={isDirty} />}
      </Box>

      {(fulfillment || order.status === OrderStatus.Accepted) && (
        <FulfilmentPanel
          order={order}
          fulfillment={fulfillment}
          onFulfillmentChanged={onFulfillmentChanged}
          canAcceptOnBehalf={canAct && isPublished}
          canAct={canAct}
          onOrderChanged={onOrderChanged}
          isDirty={isDirty}
        />
      )}
    </>
  );
}
